<script setup>
/**
 * A shared file, full screen over the room on a phone (prototype .viewer):
 * ✕, its name and Download across the top, the file itself, and a line that
 * says the session goes on behind it. It is drawn over the room and touches
 * nothing in it, so the audio and the video keep playing; closing it — ✕,
 * Escape, or a swipe down — leaves Files as it was underneath.
 *
 * The file comes through the access gate (useResourceDownload.preview). Only
 * what a browser renders without running anything is shown: images, PDFs
 * and plain text. Anything else, and a PDF on Android, whose browser draws
 * none inline, offers Download instead.
 */
import { onBeforeUnmount, onMounted, ref } from 'vue';
import useResourceDownload from '../../composables/useResourceDownload';

const props = defineProps({
  resource: { type: Object, required: true },
  downloading: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'download']);

const { preview } = useResourceDownload();

// 'loading' | 'shown' | 'unviewable' | 'failed'
const state = ref('loading');
const view = ref(null); // { type, url }

const SWIPE_CLOSE = 110;
let swipeFrom = null;

const close = () => emit('close');

const load = async () => {
  state.value = 'loading';
  try {
    const shown = await preview(props.resource);
    if (!shown || (shown.type === 'application/pdf' && /Android/i.test(navigator.userAgent))) {
      if (shown) window.URL.revokeObjectURL(shown.url);
      state.value = 'unviewable';
      return;
    }
    view.value = shown;
    state.value = 'shown';
  } catch (error) {
    console.error(error);
    state.value = 'failed';
  }
};

// Before the sheet underneath hears it: Escape closes the viewer first.
const onKeydown = (event) => {
  if (event.key !== 'Escape') return;
  event.stopPropagation();
  close();
};

const onTouchStart = (event) => {
  const scroller = event.target.closest('.mfv__page');
  swipeFrom = event.touches.length === 1 && !(scroller && scroller.scrollTop > 0) ? event.touches[0].clientY : null;
};
const onTouchEnd = (event) => {
  if (swipeFrom == null) return;
  const dy = (event.changedTouches[0]?.clientY ?? swipeFrom) - swipeFrom;
  swipeFrom = null;
  if (dy > SWIPE_CLOSE) close();
};

onMounted(() => {
  window.addEventListener('keydown', onKeydown, true);
  load();
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown, true);
  if (view.value) window.URL.revokeObjectURL(view.value.url);
});
</script>

<template>
  <div
      class="mfv"
      role="dialog"
      aria-modal="true"
      :aria-label="props.resource.name"
      data-testid="file-viewer"
      @touchstart.passive="onTouchStart"
      @touchend="onTouchEnd"
  >
    <header class="mfv__head">
      <button type="button" class="mfv__btn" aria-label="Close" title="Close" data-testid="file-viewer-close" @click="close">
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
      </button>
      <b :title="props.resource.name" data-testid="file-viewer-name">{{ props.resource.name }}</b>
      <button
          type="button"
          class="mfv__btn"
          aria-label="Download"
          title="Download"
          :disabled="props.downloading"
          :aria-busy="props.downloading ? 'true' : undefined"
          data-testid="file-viewer-download"
          @click="emit('download')"
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4.2v10.4M8 10.8l4 4 4-4" /><path d="M4.5 19.6h15" /></svg>
      </button>
    </header>

    <div class="mfv__page" :class="{ 'mfv__page--doc': state === 'shown' && !view.type.startsWith('image/') }">
      <div v-if="state === 'loading'" class="mfv__spin" role="status" aria-label="Opening the file" data-testid="file-viewer-loading" />

      <template v-else-if="state === 'shown'">
        <img v-if="view.type.startsWith('image/')" :src="view.url" :alt="props.resource.name" class="mfv__img" data-testid="file-viewer-image" />
        <iframe v-else :src="view.url" :title="props.resource.name" class="mfv__doc" data-testid="file-viewer-doc" />
      </template>

      <div v-else class="mfv__none" data-testid="file-viewer-fallback">
        <b>{{ state === 'failed' ? "The file didn't open" : "This file can't be shown here" }}</b>
        <p>{{ state === 'failed' ? 'Check your connection, or download it instead.' : 'Download it to open it on your phone.' }}</p>
        <button type="button" class="mfv__dl" :disabled="props.downloading" data-testid="file-viewer-fallback-download" @click="emit('download')">Download</button>
      </div>
    </div>

    <p class="mfv__foot" data-testid="file-viewer-foot">The session keeps playing</p>
  </div>
</template>

<style scoped>
/* prototype .viewer */
.mfv {
  position: absolute;
  inset: 0;
  z-index: 75;
  display: flex;
  flex-direction: column;
  background: #2A2530;
  color: #fff;
  font-family: var(--xl-font-family);
  animation: mfv-up 0.22s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.mfv__head {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 10px;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 12px 10px;
  background: #1C1720;
}
.mfv__head b { flex: 1; min-width: 0; overflow: hidden; font-size: 14px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }

/* prototype .iconbtn */
.mfv__btn {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  cursor: pointer;
}
.mfv__btn:active { background: rgba(255, 255, 255, 0.2); }
.mfv__btn:disabled { opacity: 0.5; cursor: progress; }
.mfv__btn:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }

.mfv__page {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  min-height: 0;
  overflow: auto;
  padding: 14px;
  -webkit-overflow-scrolling: touch;
}
.mfv__page--doc { align-items: stretch; }
.mfv__img { display: block; max-width: 100%; max-height: 100%; border-radius: 4px; object-fit: contain; box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4); }
.mfv__doc { flex: 1; width: 100%; border: 0; border-radius: 4px; background: #fff; box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4); }

.mfv__spin {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: mfv-spin 0.8s linear infinite;
}

.mfv__none { max-width: 260px; text-align: center; }
.mfv__none b { display: block; font-size: 15px; font-weight: 600; }
.mfv__none p { margin: 6px 0 16px; color: rgba(255, 255, 255, 0.72); font-size: 13px; line-height: 1.5; }
.mfv__dl {
  height: 40px;
  padding: 0 20px;
  border: 0;
  border-radius: 99px;
  background: #fff;
  color: #1C1720;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.mfv__dl:disabled { opacity: 0.6; cursor: progress; }

/* prototype .vf */
.mfv__foot {
  flex: 0 0 auto;
  margin: 0;
  padding: 12px 16px calc(14px + env(safe-area-inset-bottom, 0px));
  color: rgba(255, 255, 255, 0.72);
  font-size: 12.5px;
  text-align: center;
}

@keyframes mfv-up { from { transform: translateY(24px); opacity: 0; } }
@keyframes mfv-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .mfv, .mfv__spin { animation: none; } }
</style>
